import React from "react";
import { Navigate } from "react-router-dom";
import { useRole } from "../hooks/useRole";
import Loader from "../components/Loader/Loader";

/**
 * RoleGuard — routes that are role-gated rather than permission-gated.
 *
 * Use for Phase D AD oversight pages where we want a hasRole('AD') check
 * without standing up seeded AD permissions in admin_role first. Complements
 * ProtectedRoutes (which takes a permission path string). Pass one or more
 * role names; children render only if the user's role matches any of them.
 *
 * Unlike ProtectedRoutes, this guard does NOT wait for the permissions
 * context to load — the role list comes from the login response and is
 * available on mount. Falls back to localStorage so a page refresh doesn't flap.
 *
 * Plan §4.3 (Phase D AD cluster).
 *
 * This guard is identity-based: it admits users iff any role they hold is in
 * the supplied allow list (or the legacy 'Super Admin' bypass in
 * useRole.hasRole applies). It does NOT consult the permission matrix. A user
 * may hold several roles at once, so the check is an intersection: a session
 * holding ['AD','CCO'] passes allow={['CCO','UDP']} through its CCO role. If a
 * future role needs /ad/* access without holding 'AD' (e.g. a custom
 * AD-with-extras role granted the ad.* perms via admin_role_permissions),
 * refactor this guard to also accept a `requiredPath` prop and validate
 * against the permissions matrix the same way ProtectedRoutes does.
 */
function RoleGuard({ allow, children }) {
  const { roles, primaryRole, isAuthed, hasRole } = useRole();
  const auth = !!localStorage.getItem("accessToken") || isAuthed;

  if (!auth) {
    return <Navigate to="/login" replace />;
  }

  // The session may briefly carry no role at all between login submit and the
  // login response populating Redux. Show the loader rather than redirecting
  // to /permission, which would create a jarring flicker on the first
  // navigation. A session that resolved a primary role but an empty list is
  // still a resolved session, so it falls through to the check below.
  if (roles.length === 0 && !primaryRole) {
    return <Loader isLoading={true} />;
  }

  const allowList = Array.isArray(allow) ? allow : [allow];
  return hasRole(...allowList) ? children : <Navigate to="/permission" replace />;
}

export default RoleGuard;
